'use client';

import { useModal } from '@ebay/nice-modal-react';
import { Form, Formik, type FormikHelpers } from 'formik';
import { useLocale, useTranslations } from 'next-intl';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';

import { FormCaptcha } from '@/components/forms/fields/FormCaptcha';
import { FormInput } from '@/components/forms/fields/FormInput';
import { FormSubmitButton } from '@/components/forms/fields/FormSubmitButton';
import type { EntityType } from '@/components/ProfileHeader/types';
import { useSession } from '@/hooks/auth/useSession';
import { subscribeToEntityAlertAction } from '@/lib/actions/subscribeToEntityAlertAction';
import { ENTITY_ALERT_CONFIG } from '@/lib/alerts/entityAlertConfig';
import type { PossibleLocales } from '@/utils/AppConfig';

import { InfoModal } from '../modals/InfoModal';

interface FormPayload {
  guest_email: string;
  guest_lang: PossibleLocales;
  altcha_token: string;
}

interface SubscribeEntityAlertFormProps {
  entityType: EntityType;
  /** One or more entity ids. Single entities pass a 1-element array. */
  entityIds: number[];
  entityName: string;
  onSuccess?: () => void;
}

export const SubscribeEntityAlertForm: React.FC<
  SubscribeEntityAlertFormProps
> = ({ entityType, entityIds, entityName, onSuccess }) => {
  const locale = useLocale() as PossibleLocales;
  const { sessionData } = useSession();

  const [initialValues, setInitialValues] = useState<FormPayload>({
    guest_email: sessionData?.user.email || '',
    guest_lang: locale,
    altcha_token: '',
  });

  useEffect(() => {
    if (sessionData && sessionData.user) {
      setInitialValues({
        guest_email: sessionData.user.email,
        guest_lang: locale,
        altcha_token: '',
      });
    }
  }, [sessionData, locale]);

  const t = useTranslations('Blocs.FormulaireInscriptionAlerteEntite');
  const [isLoading, setIsLoading] = useState(false);
  const infoModal = useModal(InfoModal);

  const formSchema = Yup.object().shape({
    guest_email: Yup.string()
      .email(t('Messages.erreur_email_invalide'))
      .required(t('Messages.erreur_email_requis'))
      .test('is-valid-email', t('Messages.erreur_email_invalide'), (value) => {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return value ? emailRegex.test(value) : false;
      }),
  });
  // Get the appropriate confirmation message based on entity type
  const getConfirmationMessageKey = () =>
    ENTITY_ALERT_CONFIG[entityType].confirmKey;

  const onSubmit = async (
    data: FormPayload,
    { resetForm }: FormikHelpers<FormPayload>,
  ) => {
    setIsLoading(true);

    try {
      const subscribed = await subscribeToEntityAlertAction({
        entityType,
        entityIds,
        guest_email: data.guest_email,
        altcha_token: data.altcha_token,
      });

      if (!subscribed) {
        infoModal.show({
          title: t('Messages.titre_erreur_inscription'),
          message: t('Messages.erreur_inscription'),
          showCancel: false,
          confirmLabel: t('label_confirmer'),
        });
      } else {
        resetForm({ values: initialValues });
        infoModal.show({
          title: t('Messages.titre_confirmation_inscription'),
          message: t(getConfirmationMessageKey()),
          showCancel: false,
          confirmLabel: t('label_confirmer'),
        });
        onSuccess?.();
      }
    } catch (err) {
      console.error(err);
      infoModal.show({
        title: t('Messages.titre_erreur_inscription'),
        message: t('Messages.erreur_inscription'),
        showCancel: false,
        confirmLabel: t('label_confirmer'),
      });
    }
    setIsLoading(false);
  };

  // Get the appropriate intro text based on entity type
  const getIntroKey = () => ENTITY_ALERT_CONFIG[entityType].introKey;

  return (
    <div>
      <div className="mb-3 text-base">{t(getIntroKey())}</div>
      <Formik
        initialValues={initialValues}
        validationSchema={formSchema}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ isValid }) => (
          <Form className="space-y-4">
            <FormInput
              name="guest_email"
              type="email"
              autoCorrect={false}
              required
              placeholder={t('Champs.placeholder_email')}
            />

            <FormCaptcha />
            <FormSubmitButton
              isLoading={isLoading}
              isDisabled={!isValid}
              text={t('Champs.texte_bouton_envoyer')}
            />
          </Form>
        )}
      </Formik>
    </div>
  );
};
